// FormBuilderCanvas — the builder's center playground: a toolbar (flow button ·
// page pills + add page · Collapse/Expand all) over the active page's items.
// State lives in FormBuilderPage; this renders and dispatches. Each page pill
// carries a chevron menu (Duplicate — the same deep page copy as the Manage
// pages drawer — Move left/right — direction-aware so "left" is always
// visually left — and Delete page behind a danger confirm; rename lives in the
// page-properties panel the pill click opens).
//
// The canvas renders the NESTED view of the flat model (deriveCanvasItems):
// top-level field cards and section CONTAINER cards, each section grouping its
// trailing fields per the flat wire contract. Numbering is hierarchical — the
// top-level position for items ("5"), section children as "5.1", "5.2"….
//
// Drops land on persistent FormBuilderDropGap elements (each owns its drag
// events — a gap expands exactly while hovered): the spacing gaps between
// top-level items, the gaps between a section's children, a section's empty
// "Drag fields here" panel, and the empty-canvas placeholder. Top-level gaps
// take anything — a field dropped between/after sections stays TOP-LEVEL
// (membership is the field's sectionId, confirmed) — while section bodies
// take fields only (sections never nest) and stamp the section's id on the
// drop. The same gaps also take REORDER drags (a card's grip): the drop calls
// onMoveExisting with the dragged row's id and the gap's bound position, and
// while a drag is live the two gaps hugging the dragged block are disabled
// (their drop would be a no-op). A COLLAPSED section renders no body, so
// dropping INTO one means expanding it first (deliberate — its trailing
// top-level gap keeps meaning "after the block, top-level", which is exactly
// the "move out of section" target). The toolbar's first icon opens the
// "Manage pages" drawer (FormPagesDrawer, owned by FormBuilderPage).

import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, Dropdown, Icon, IconButton } from '@/components/common';
import { isRTL } from '@/config/config';
import type { LibraryElementType } from './utils/formElementDefaults';
import type { BuilderPage } from './utils/builderModel';
import {
  deriveCanvasItems,
  fieldBlockRange,
  sectionBlockRange,
  type CanvasSectionItem,
} from './utils/canvasItems';
import { isLogicEligible } from './utils/triggerOps';
import FormBuilderDropGap from './FormBuilderDropGap';
import FormBuilderFieldCard from './FormBuilderFieldCard';
import FormBuilderSectionCard from './FormBuilderSectionCard';

interface FormBuilderCanvasProps {
  pages: BuilderPage[];
  activePageId: string;
  /** Currently selected field (highlighted; shown in the properties panel). */
  selectedFieldId: string | null;
  onSelectField: (fieldId: string) => void;
  onSelectPage: (id: string) => void;
  /** A library item was dropped at flat index `index` of the active page —
   *  inside the section named by `sectionId`, or top-level when omitted. */
  onDropField: (type: LibraryElementType, index: number, sectionId?: string | null) => void;
  /** An existing row was dropped at flat index `index` — moved with its whole
   *  block into the section named by `sectionId`, or top-level when omitted. */
  onMoveExisting: (id: string, index: number, sectionId?: string | null) => void;
  /** Open the conditional-logic drawer for a field. */
  onOpenLogic: (fieldId: string) => void;
  /** Open the "Manage pages" drawer. */
  onManagePages: () => void;
  onAddPage: () => void;
  /** Swap a page with its neighbor (model order; -1 = earlier). */
  onMovePage: (id: string, dir: -1 | 1) => void;
  /** Deep-copy a page (every field + its properties), inserted after it. */
  onDuplicatePage: (id: string) => void;
  onDeletePage: (id: string) => void;
  /** Set the collapsed flag on every field of the active page. */
  onCollapseAll: (collapsed: boolean) => void;
  onToggleCollapse: (fieldId: string) => void;
  /** Swap a field with its neighbor, bounded inside its group. */
  onMove: (fieldId: string, dir: -1 | 1) => void;
  onDuplicate: (fieldId: string) => void;
  onDelete: (fieldId: string) => void;
  /** Section block ops (row + children as one unit). */
  onMoveSection: (sectionId: string, dir: -1 | 1) => void;
  onDuplicateSection: (sectionId: string) => void;
  onDeleteSection: (sectionId: string) => void;
}

const FormBuilderCanvas = ({
  pages,
  activePageId,
  selectedFieldId,
  onSelectField,
  onSelectPage,
  onDropField,
  onMoveExisting,
  onOpenLogic,
  onManagePages,
  onAddPage,
  onMovePage,
  onDuplicatePage,
  onDeletePage,
  onCollapseAll,
  onToggleCollapse,
  onMove,
  onDuplicate,
  onDelete,
  onMoveSection,
  onDuplicateSection,
  onDeleteSection,
}: FormBuilderCanvasProps) => {
  const { t } = useTranslation();
  // One open pill menu at a time; deletes go through danger confirms.
  const [menuPageId, setMenuPageId] = useState<string | null>(null);
  const [confirmPageId, setConfirmPageId] = useState<string | null>(null);
  const [confirmSectionId, setConfirmSectionId] = useState<string | null>(null);
  // A field whose deletion cascades to its follow-up questions confirms first.
  const [confirmFieldId, setConfirmFieldId] = useState<string | null>(null);

  const activePage = pages.find((p) => p.id === activePageId) ?? pages[0];
  const fields = activePage?.fields ?? [];
  const items = deriveCanvasItems(fields);
  const allCollapsed = fields.length > 0 && fields.every((f) => f.collapsed);

  // Live reorder drag: the dragged row's id (set by the source card's grip
  // drag, cleared by its dragend — which fires on cancel too). The two gaps
  // hugging the dragged block are disabled: same membership AND flush against
  // it (leading gaps bind the block's start, trailing gaps its end — equal to
  // the next item's start by contiguity). The membership check deliberately
  // keeps the top-level gap after a section ENABLED while dragging that
  // section's last member: that drop moves it OUT in place.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // The source's own dragend is NOT enough: a move across subtrees (top-level
  // ↔ section body) remounts the card under its new parent, and Chrome skips
  // dragend on a removed source node — draggingId would stick and ghost the
  // remounted card. The drop event always fires in-document BEFORE the state
  // mutation, and a cancelled drag keeps its source node alive for dragend,
  // so clearing on either signal at the document level covers every ending.
  useEffect(() => {
    if (!draggingId) return undefined;
    const clear = () => setDraggingId(null);
    document.addEventListener('drop', clear);
    document.addEventListener('dragend', clear);
    return () => {
      document.removeEventListener('drop', clear);
      document.removeEventListener('dragend', clear);
    };
  }, [draggingId]);
  const dragRow = draggingId ? fields.find((f) => f.id === draggingId) : undefined;
  const dragRange = dragRow
    ? (dragRow.elementType === 'section' ? sectionBlockRange : fieldBlockRange)(fields, dragRow.id)
    : null;
  const gapDisabled = (index: number, sectionId: string | null) =>
    !!dragRange &&
    !!dragRow &&
    sectionId === dragRow.sectionId &&
    (index === dragRange[0] || index === dragRange[1]);
  const dragStateFor = (id: string) => (dragging: boolean) => setDraggingId(dragging ? id : null);

  const pageName = (p: BuilderPage) =>
    p.properties.basicProperties.pageTitle || t('forms_builder_untitled_page');
  // "Move left" must move the pill visually left — in RTL that's index +1.
  const leftDelta: -1 | 1 = isRTL() ? 1 : -1;
  const confirmIndex = pages.findIndex((p) => p.id === confirmPageId);
  const confirmPage = confirmIndex >= 0 ? pages[confirmIndex] : null;
  const confirmSection =
    items.find(
      (it): it is CanvasSectionItem => it.kind === 'section' && it.section.id === confirmSectionId,
    ) ?? null;
  const confirmField = fields.find((f) => f.id === confirmFieldId) ?? null;
  const confirmFieldFollowUps = confirmField
    ? (() => {
        const range = fieldBlockRange(fields, confirmField.id);
        return range ? range[1] - range[0] - 1 : 0;
      })()
    : 0;

  // Deleting a field with follow-up questions cascades — confirm first;
  // an ordinary field deletes directly, as before.
  const requestDeleteField = (fieldId: string) => {
    const range = fieldBlockRange(fields, fieldId);
    if (range && range[1] - range[0] > 1) setConfirmFieldId(fieldId);
    else onDelete(fieldId);
  };

  // A section's body: leading/inner/trailing gaps around the child cards, or
  // the empty "Drag fields here" panel. Field drops only — sections can't nest.
  const sectionBody = (item: CanvasSectionItem, pos: number) => {
    if (!item.children.length) {
      return (
        <FormBuilderDropGap
          accept='field'
          block='form-builder-section__dropzone'
          onDropField={(type) => onDropField(type, item.flat + 1, item.section.id)}
          onMoveField={(id) => onMoveExisting(id, item.flat + 1, item.section.id)}
        >
          {() => (
            <>
              <Icon name='circle-plus' size={24} />
              <span className='form-builder-section__dropzone-title'>
                {t('forms_builder_section_drag_here')}
              </span>
              <span className='form-builder-section__dropzone-hint'>
                {t('forms_builder_section_drop_hint')}
              </span>
            </>
          )}
        </FormBuilderDropGap>
      );
    }
    const lastChild = item.children[item.children.length - 1];
    return (
      <div className='form-builder-section__list'>
        {item.children.map((child, k) => (
          <Fragment key={child.field.id}>
            <FormBuilderDropGap
              accept='field'
              onDropField={(type) => onDropField(type, child.flat, item.section.id)}
              onMoveField={(id) => onMoveExisting(id, child.flat, item.section.id)}
              disabled={gapDisabled(child.flat, item.section.id)}
            />
            <FormBuilderFieldCard
              field={child.field}
              badge={`${pos + 1}.${k + 1}`}
              isFirst={k === 0}
              isLast={k === item.children.length - 1}
              selected={child.field.id === selectedFieldId}
              dragging={draggingId === child.field.id}
              onDragStateChange={dragStateFor(child.field.id)}
              logicEligible={isLogicEligible(child.field)}
              onOpenLogic={() => onOpenLogic(child.field.id)}
              followUps={child.followUps}
              selectedFieldId={selectedFieldId}
              onSelectChild={onSelectField}
              onSelect={() => onSelectField(child.field.id)}
              onToggleCollapse={() => onToggleCollapse(child.field.id)}
              onMove={(dir) => onMove(child.field.id, dir)}
              onDuplicate={() => onDuplicate(child.field.id)}
              onDelete={() => requestDeleteField(child.field.id)}
            />
          </Fragment>
        ))}
        {/* Block end, not flat + 1 — the last member's follow-up rows sit
         * between, and a drop must land after them, never inside the block. */}
        <FormBuilderDropGap
          accept='field'
          onDropField={(type) => onDropField(type, lastChild.end, item.section.id)}
          onMoveField={(id) => onMoveExisting(id, lastChild.end, item.section.id)}
          disabled={gapDisabled(lastChild.end, item.section.id)}
        />
      </div>
    );
  };

  return (
    <div className='form-builder-canvas'>
      <div className='form-builder-canvas__toolbar'>
        <IconButton
          icon='workflow'
          variant='outline'
          aria-label={t('forms_builder_pages_title')}
          onClick={onManagePages}
          size='sm'
        />

        <div className='form-builder-canvas__pages'>
          {pages.map((p, i) => (
            <div
              key={p.id}
              className={[
                'form-builder-canvas__page-pill',
                p.id === activePage?.id && 'form-builder-canvas__page-pill--active',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type='button'
                className='form-builder-canvas__page-label'
                onClick={() => onSelectPage(p.id)}
              >
                {pageName(p)}
              </button>
              <Dropdown
                open={menuPageId === p.id}
                onClose={() => setMenuPageId(null)}
                align='start'
                width={200}
                trigger={
                  <button
                    type='button'
                    className='form-builder-canvas__page-menu'
                    aria-label={t('forms_builder_page_menu')}
                    aria-expanded={menuPageId === p.id}
                    onClick={() => setMenuPageId((prev) => (prev === p.id ? null : p.id))}
                  >
                    <Icon name='chevron-down' size={14} />
                  </button>
                }
              >
                <button
                  type='button'
                  className='app-dropdown__item'
                  role='menuitem'
                  onClick={() => {
                    onDuplicatePage(p.id);
                    setMenuPageId(null);
                  }}
                >
                  <Icon name='copy' size={15} />
                  <span className='app-dropdown__item-label'>
                    {t('forms_builder_page_duplicate')}
                  </span>
                </button>
                <button
                  type='button'
                  className='app-dropdown__item'
                  role='menuitem'
                  disabled={i + leftDelta < 0 || i + leftDelta >= pages.length}
                  onClick={() => {
                    onMovePage(p.id, leftDelta);
                    setMenuPageId(null);
                  }}
                >
                  <Icon name='arrow-left' size={15} />
                  <span className='app-dropdown__item-label'>
                    {t('forms_builder_page_move_left')}
                  </span>
                </button>
                <button
                  type='button'
                  className='app-dropdown__item'
                  role='menuitem'
                  disabled={i - leftDelta < 0 || i - leftDelta >= pages.length}
                  onClick={() => {
                    onMovePage(p.id, -leftDelta as -1 | 1);
                    setMenuPageId(null);
                  }}
                >
                  <Icon name='arrow-right' size={15} />
                  <span className='app-dropdown__item-label'>
                    {t('forms_builder_page_move_right')}
                  </span>
                </button>
                {pages.length > 1 && (
                  <>
                    <div className='app-dropdown__divider' role='separator' />
                    <button
                      type='button'
                      className='app-dropdown__item app-dropdown__item--danger'
                      role='menuitem'
                      onClick={() => {
                        setMenuPageId(null);
                        setConfirmPageId(p.id);
                      }}
                    >
                      <Icon name='trash-2' size={15} />
                      <span className='app-dropdown__item-label'>
                        {t('forms_builder_page_delete')}
                      </span>
                    </button>
                  </>
                )}
              </Dropdown>
            </div>
          ))}
          <IconButton
            icon='plus'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_add_page')}
            onClick={onAddPage}
          />
        </div>

        <Button
          variant='secondary'
          iconStart='chevrons-down-up'
          onClick={() => onCollapseAll(!allCollapsed)}
          size='sm'
        >
          {t(allCollapsed ? 'forms_builder_expand_all' : 'forms_builder_collapse_all')}
        </Button>
      </div>

      {fields.length === 0 ? (
        <FormBuilderDropGap
          accept='any'
          block='form-builder-canvas__empty'
          onDropField={(type) => onDropField(type, 0)}
        >
          {(hovering) =>
            hovering ? (
              t('forms_builder_drop_here')
            ) : (
              <>
                <Icon name='clipboard-list' size={28} />
                {t('forms_builder_canvas_empty')}
              </>
            )
          }
        </FormBuilderDropGap>
      ) : (
        <div className='form-builder-canvas__list'>
          {items.map((item, pos) => (
            <Fragment key={item.kind === 'section' ? item.section.id : item.field.id}>
              <FormBuilderDropGap
                accept='any'
                className={pos === 0 ? 'form-builder-canvas__drop-gap--edge-start' : undefined}
                onDropField={(type) => onDropField(type, item.flat)}
                onMoveField={(id) => onMoveExisting(id, item.flat)}
                disabled={gapDisabled(item.flat, null)}
              />
              {item.kind === 'section' ? (
                <FormBuilderSectionCard
                  section={item.section}
                  badge={`${pos + 1}`}
                  count={item.children.length}
                  isFirst={pos === 0}
                  isLast={pos === items.length - 1}
                  selected={item.section.id === selectedFieldId}
                  dragging={draggingId === item.section.id}
                  onDragStateChange={dragStateFor(item.section.id)}
                  onSelect={() => onSelectField(item.section.id)}
                  onToggleCollapse={() => onToggleCollapse(item.section.id)}
                  onMove={(dir) => onMoveSection(item.section.id, dir)}
                  onDuplicate={() => onDuplicateSection(item.section.id)}
                  onDelete={() => setConfirmSectionId(item.section.id)}
                >
                  {sectionBody(item, pos)}
                </FormBuilderSectionCard>
              ) : (
                <FormBuilderFieldCard
                  field={item.field}
                  badge={`${pos + 1}`}
                  isFirst={pos === 0}
                  isLast={pos === items.length - 1}
                  selected={item.field.id === selectedFieldId}
                  dragging={draggingId === item.field.id}
                  onDragStateChange={dragStateFor(item.field.id)}
                  logicEligible={isLogicEligible(item.field)}
                  onOpenLogic={() => onOpenLogic(item.field.id)}
                  followUps={item.followUps}
                  selectedFieldId={selectedFieldId}
                  onSelectChild={onSelectField}
                  onSelect={() => onSelectField(item.field.id)}
                  onToggleCollapse={() => onToggleCollapse(item.field.id)}
                  onMove={(dir) => onMove(item.field.id, dir)}
                  onDuplicate={() => onDuplicate(item.field.id)}
                  onDelete={() => requestDeleteField(item.field.id)}
                />
              )}
            </Fragment>
          ))}
          <FormBuilderDropGap
            accept='any'
            className='form-builder-canvas__drop-gap--edge-end'
            onDropField={(type) => onDropField(type, fields.length)}
            onMoveField={(id) => onMoveExisting(id, fields.length)}
            disabled={gapDisabled(fields.length, null)}
          />
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirmPage)}
        onClose={() => setConfirmPageId(null)}
        onConfirm={() => {
          if (confirmPage) onDeletePage(confirmPage.id);
          setConfirmPageId(null);
        }}
        tone='danger'
        icon='trash-2'
        title={t('forms_builder_page_delete_title')}
        description={
          confirmPage
            ? t('forms_builder_page_delete_desc', {
                name: pageName(confirmPage),
                count: confirmPage.fields.length,
              })
            : ''
        }
        confirmLabel={t('forms_builder_page_delete')}
        cancelLabel={t('button_cancel')}
        confirmVariant='danger'
      />

      {/* Deleting a section removes its fields with it (confirmed). */}
      <ConfirmModal
        open={Boolean(confirmSection)}
        onClose={() => setConfirmSectionId(null)}
        onConfirm={() => {
          if (confirmSection) onDeleteSection(confirmSection.section.id);
          setConfirmSectionId(null);
        }}
        tone='danger'
        icon='trash-2'
        title={t('forms_builder_section_delete_title')}
        description={
          confirmSection
            ? t('forms_builder_section_delete_desc', {
                name:
                  ((confirmSection.section.properties.basicProperties as Record<string, unknown>)
                    .sectionTitle as string) || t('forms_field_section'),
                count: confirmSection.children.length,
              })
            : ''
        }
        confirmLabel={t('forms_builder_section_delete')}
        cancelLabel={t('button_cancel')}
        confirmVariant='danger'
      />

      {/* Deleting a field takes its conditional-logic follow-ups with it. */}
      <ConfirmModal
        open={Boolean(confirmField)}
        onClose={() => setConfirmFieldId(null)}
        onConfirm={() => {
          if (confirmField) onDelete(confirmField.id);
          setConfirmFieldId(null);
        }}
        tone='danger'
        icon='trash-2'
        title={t('forms_builder_field_delete_cascade_title')}
        description={
          confirmField
            ? t('forms_builder_field_delete_cascade_desc', {
                name:
                  (
                    (confirmField.properties.basicProperties as Record<string, unknown>)
                      .fieldTitle as string
                  )?.trim() || t('forms_builder_untitled_field'),
                count: confirmFieldFollowUps,
              })
            : ''
        }
        confirmLabel={t('forms_builder_field_delete')}
        cancelLabel={t('button_cancel')}
        confirmVariant='danger'
      />
    </div>
  );
};

export default FormBuilderCanvas;
